import type {
  DestinationEditSection,
  DestinationValidationError,
} from '@/features/destination/destination-form-model'
import type {
  DestinationCenterPoint,
  DestinationFormValue,
  DestinationTranslationValue,
  IAreaBoundary,
  UpdateDestinationPayload,
} from '@/shared/api/types/destination-types'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateDestinationMutation, useUpdateDestinationMutation } from '@/entities/destination'
import {
  buildCreateDestinationPayload,
  buildDetailsUpdatePayload,
  buildMapUpdatePayload,
  buildTranslationsUpdatePayload,
  normalizeDestinationTranslations,
  resolveTranslationRows,
  validateDestinationDetails,
  validateDestinationMap,
  validateDestinationSubmission,
  validateDestinationTranslations,
} from '@/features/destination/destination-form-model'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'

interface UseDestinationFormActionsParams {
  destinationId: string
  isEdit: boolean
  /** Where the list lives for the current scope (super admin vs tenant); used for Back and after a save. */
  listPath: string
  form: DestinationFormValue
  translations: DestinationTranslationValue[]
  supportedLanguages: string[]
  /** Banner or thumbnail is still uploading. */
  isMediaUploading: boolean
  clearCurrentJobs: () => void
  /** Lets the next route change through the unsaved-changes guard (after a successful save). */
  allowNextNavigation: () => void
  commitBaseline: {
    translations: () => void
    details: () => void
    map: (boundary: IAreaBoundary, center: DestinationCenterPoint) => void
  }
}

/** Create flow (whole form at once) and the independent per-section saves of the edit page. */
export function useDestinationFormActions({
  destinationId,
  isEdit,
  listPath,
  form,
  translations,
  supportedLanguages,
  isMediaUploading,
  clearCurrentJobs,
  allowNextNavigation,
  commitBaseline,
}: UseDestinationFormActionsParams) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createMutation = useCreateDestinationMutation()
  const updateMutation = useUpdateDestinationMutation()
  const [savingSection, setSavingSection] = useState<DestinationEditSection | null>(null)

  function showValidationError(error: DestinationValidationError) {
    showToast.error(
      error.defaultValue ? t(error.key, { defaultValue: error.defaultValue }) : t(error.key),
    )
  }

  function navigateToList() {
    void navigate({ href: listPath })
  }

  /**
   * Create flow: validate the whole form, create the destination and go back to the list.
   * (Editing never goes through here — each edit section has its own save below.)
   */
  async function handleSubmit() {
    if (isEdit) return

    const normalizedTranslations = normalizeDestinationTranslations(
      resolveTranslationRows(translations, supportedLanguages),
    )
    const validationError = validateDestinationSubmission({
      form,
      isUploading: isMediaUploading,
      translations: normalizedTranslations,
      validateTranslations: true,
    })
    const { areaBoundary, centerPoint } = form
    if (validationError || !areaBoundary || !centerPoint) {
      // Boundary and centre are non-null whenever validation passed; the check only narrows them for the builder.
      if (validationError) showValidationError(validationError)
      return
    }

    try {
      await createMutation.mutateAsync(
        buildCreateDestinationPayload(form, areaBoundary, centerPoint, normalizedTranslations),
      )
      clearCurrentJobs()
      allowNextNavigation()
      navigateToList()
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  /** Shared wrapper of the edit-page section saves: one save at a time, loading flag and toasts; resolves to `true` when saved. */
  async function saveEditSection(
    section: DestinationEditSection,
    payload: UpdateDestinationPayload,
  ): Promise<boolean> {
    if (!destinationId || savingSection !== null) return false

    try {
      setSavingSection(section)
      await updateMutation.mutateAsync({ id: destinationId, payload })
      showToast.success(t('destination.updateSuccess'))
      return true
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
      return false
    } finally {
      setSavingSection(null)
    }
  }

  // * VI: Chỉ cập nhật baseline khi API thành công, để dirty-check luôn phản ánh đúng dữ liệu đã lưu. | EN: Baselines are updated only after the API succeeded, so dirty checks always reflect what is really saved.
  async function handleSaveTranslations() {
    const normalizedTranslations = normalizeDestinationTranslations(
      resolveTranslationRows(translations, supportedLanguages),
    )
    const validationError = validateDestinationTranslations(normalizedTranslations)
    if (validationError) {
      showValidationError(validationError)
      return
    }
    if (
      await saveEditSection('translations', buildTranslationsUpdatePayload(normalizedTranslations))
    ) {
      commitBaseline.translations()
    }
  }

  async function handleSaveDetails() {
    const validationError = validateDestinationDetails(form, isMediaUploading)
    if (validationError) {
      showValidationError(validationError)
      return
    }
    if (await saveEditSection('details', buildDetailsUpdatePayload(form))) {
      commitBaseline.details()
    }
  }

  /** The map picker owns its drawing state and hands the final boundary/centre to this callback on "Save". */
  async function handleSaveMap(
    boundary: IAreaBoundary | null,
    center: DestinationCenterPoint | null,
  ) {
    if (!isEdit) return
    const validationError = validateDestinationMap(boundary, center)
    if (validationError || !boundary || !center) {
      if (validationError) showValidationError(validationError)
      return
    }
    if (await saveEditSection('map', buildMapUpdatePayload(boundary, center))) {
      commitBaseline.map(boundary, center)
    }
  }

  return {
    savingSection,
    isSaving: createMutation.isPending || updateMutation.isPending || isMediaUploading,
    navigateToList,
    handleSubmit,
    handleSaveTranslations,
    handleSaveDetails,
    handleSaveMap,
  }
}
